import { Injectable, signal, OnDestroy } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { HttpService } from '../../../theme/shared/service/http.service';
import { AuthenticationService } from '../../../theme/shared/service/authentication.service';

export interface MaintenanceNotification {
  id: number;
  message: string;
  is_read: boolean;
  created_at: string;
  event_type?: string;
  work_order_id?: number;
}

/**
 * NotificationService - Maintenance Module
 *
 * Manages the in-app notification bell for the Maintenance module.
 * - Loads existing notifications from GET /api/maintenance-notifications/ on first use.
 * - Subscribes to AuthenticationService.notification$ for real-time WebSocket pushes.
 * - Exposes reactive signals consumed by the nav-right notification bell component.
 * - Broadcasts real-time notification events via notificationEvent$ for auto-refreshing active pages.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceNotificationService implements OnDestroy {
  private readonly BASE = 'api';

  /** Reactive list of the most recent notifications (newest first). */
  public notifications = signal<MaintenanceNotification[]>([]);

  /** Number of unread notifications. Drives the badge counter on the bell icon. */
  public unreadCount = signal<number>(0);

  /** Event bus for real-time notification events. Active components subscribe to auto-refresh data. */
  private notificationEventSubject = new Subject<any>();
  public notificationEvent$ = this.notificationEventSubject.asObservable();

  /** Whether the initial data has been loaded from the server. */
  private loaded = false;

  private wsSub?: Subscription;

  constructor(
    private api: HttpService,
    private authService: AuthenticationService
  ) {
    // Subscribe to real-time WebSocket notification events immediately.
    // When a NOTIFICATION message arrives, prepend it to the list, increment unread count,
    // and broadcast the event to active components for auto-refreshing.
    this.wsSub = this.authService.notification$.subscribe((data: any) => {
      const newNotif: MaintenanceNotification = {
        id: Date.now(),
        message: data.message || '',
        is_read: false,
        created_at: new Date().toISOString(),
        event_type: data.event_type || 'NOTIFICATION',
        work_order_id: data.work_order_id,
      };
      this.notifications.update(list => [newNotif, ...list.slice(0, 19)]);
      this.unreadCount.update(count => count + 1);
      this.notificationEventSubject.next(data);
    });
  }

  /**
   * Load the initial notification list and unread count from the server.
   * Idempotent: only fetches once per session unless force is true.
   */
  loadNotifications(force = false): void {
    if (this.loaded && !force) return;
    this.loaded = true;

    this.api.getData(`${this.BASE}/maintenance-notifications/`).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        this.notifications.set(data?.notifications || []);
        this.unreadCount.set(data?.unread_count ?? 0);
      },
      error: () => {
        // Silently ignore - notification failure must not affect main UI
      }
    });
  }

  /**
   * Mark all notifications as read on the server and clear the local badge counter.
   */
  markAllRead(): void {
    if (this.unreadCount() === 0) return;

    this.api.patchData(`${this.BASE}/maintenance-notifications/mark-read/`, {}).subscribe({
      next: () => {
        this.unreadCount.set(0);
        this.notifications.update(list =>
          list.map(n => ({ ...n, is_read: true }))
        );
      },
      error: () => {
        // Silently ignore
      }
    });
  }

  ngOnDestroy(): void {
    this.wsSub?.unsubscribe();
  }
}
